/**
 * ExchangeRateService — Taux de change USD/EUR via Frankfurter (ECB)
 *
 * API gratuite, sans clé, données ECB (Banque centrale européenne) mises à
 * jour quotidiennement.
 *
 * Cache local 24h (AsyncStorage) → 1 appel API par jour par device.
 * Fallback hardcodé 1 USD = 0.92 EUR si réseau indisponible.
 *
 * Usage :
 *   import { getUsdToEur, convertUsdToEur } from '../services/ExchangeRateService';
 *   const rate = await getUsdToEur();         // ex. 0.9234
 *   const eur  = await convertUsdToEur(100);  // ex. 92.34
 *
 * v1.12.336
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const FRANKFURTER_URL = 'https://api.frankfurter.app/latest?from=USD&to=EUR';
// v1.12.485 — Frankfurter ne renvoie pas les headers CORS pour les origins
// arbitraires (mytenue.com). Sur web on évite l'appel et on utilise le
// fallback hardcodé. Sur mobile (iOS/Android natif), pas de CORS donc OK.
const IS_WEB = Platform.OS === 'web';
const CACHE_KEY       = 'exchange_rate_usd_eur';
const CACHE_TTL_MS    = 24 * 60 * 60 * 1000; // 24h
const FALLBACK_RATE   = 0.92;

let inMemoryRate = null;
let inMemoryFetchedAt = 0;

/** Retourne le taux courant USD→EUR (1 USD = X EUR). Cache 24h. */
export async function getUsdToEur() {
  const now = Date.now();
  // 1. Mémoire (intra-session)
  if (inMemoryRate && (now - inMemoryFetchedAt) < CACHE_TTL_MS) {
    return inMemoryRate;
  }
  // 2. AsyncStorage (persistant)
  try {
    const cached = await AsyncStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.rate && parsed?.fetchedAt && (now - parsed.fetchedAt) < CACHE_TTL_MS) {
        inMemoryRate = parsed.rate;
        inMemoryFetchedAt = parsed.fetchedAt;
        return parsed.rate;
      }
    }
  } catch {}
  // 3. Fetch frais depuis Frankfurter — skip sur web (CORS bloqué)
  if (IS_WEB) {
    return FALLBACK_RATE;
  }
  try {
    const res = await fetch(FRANKFURTER_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const rate = data?.rates?.EUR;
    if (typeof rate === 'number' && rate > 0 && rate < 2) {
      inMemoryRate = rate;
      inMemoryFetchedAt = now;
      try {
        await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ rate, fetchedAt: now }));
      } catch {}
      return rate;
    }
    throw new Error('invalid_rate');
  } catch (e) {
    console.warn('[ExchangeRate] fetch failed, using fallback:', e?.message);
    return FALLBACK_RATE;
  }
}

/** Convertit un montant USD en EUR via le taux courant. */
export async function convertUsdToEur(usd) {
  const rate = await getUsdToEur();
  return (Number(usd) || 0) * rate;
}

/** Version sync utilisant le cache mémoire — utile dans les renders.
 *  Retourne FALLBACK si le cache n'est pas chargé.
 */
export function getUsdToEurSync() {
  return inMemoryRate || FALLBACK_RATE;
}

export function convertUsdToEurSync(usd) {
  return (Number(usd) || 0) * getUsdToEurSync();
}
